import type { CurrentStarterInfo } from '@racemap/utilities/api-client';
import { OneMinuteInMillis, OneSecondInMillis } from '@racemap/utilities/consts/time';

export type Reading = CurrentStarterInfo['current'];

/** The instants the chart is built around, in epoch ms. */
export type GapTimes = {
  resultTime: number | null;
  gapCalcTime: number | null;
  /** The leader's own reading set gapCalcTime, so the gaps are measured from its side. */
  leaderDefinesGapCalcTime: boolean;
};

export function gapTimes(
  starter: Reading | null,
  leader: Reading | null,
  resultTime: number | null,
): GapTimes {
  const leaderTime = leader != null ? new Date(leader.time).getTime() : null;
  const gapCalcTime = starter?.gapCalcTime != null ? new Date(starter.gapCalcTime).getTime() : null;
  return {
    resultTime,
    gapCalcTime,
    leaderDefinesGapCalcTime:
      leaderTime != null && gapCalcTime != null && leaderTime === gapCalcTime,
  };
}

/** Maps time and offset to the normalised plot [0,1]×[0,1], y up. */
export type Scales = {
  minTime: number;
  maxTime: number;
  minOffset: number;
  maxOffset: number;
  timeRange: number;
  offsetRange: number;
  toX: (time: number) => number;
  toY: (offset: number) => number;
};

/** Fits both axes to every instant and offset the chart draws, with padding. */
export function buildScales(
  starter: Reading | null,
  leader: Reading | null,
  { resultTime, gapCalcTime }: GapTimes,
): Scales | null {
  if (starter == null || resultTime == null) return null;
  const time = new Date(starter.time).getTime();

  const allMillis: Array<number> = [time, resultTime];
  const allOffsets: Array<number> = [];
  if (leader?.fromStart != null) {
    allOffsets.push(leader.fromStart);
    if (starter.gapDistance != null) allOffsets.push(leader.fromStart - starter.gapDistance);
  }
  if (gapCalcTime != null) allMillis.push(gapCalcTime);
  if (starter.gap != null) allMillis.push(time - OneSecondInMillis * starter.gap);
  if (starter.gapChase != null) allMillis.push(time + OneSecondInMillis * starter.gapChase);
  if (starter.gapAverage != null) allMillis.push(time - OneSecondInMillis * starter.gapAverage);
  if (starter.gapEx != null) allMillis.push(resultTime - OneSecondInMillis * starter.gapEx);
  if (starter.gapChaseEx != null)
    allMillis.push(resultTime + OneSecondInMillis * starter.gapChaseEx);
  if (starter.gapAverageEx != null)
    allMillis.push(resultTime - OneSecondInMillis * starter.gapAverageEx);
  if (starter.fromStart != null) allOffsets.push(starter.fromStart);
  if (starter.fromStartEx != null) allOffsets.push(starter.fromStartEx);
  if (starter.fromStart != null && starter.gapDistance != null)
    allOffsets.push(starter.fromStart + starter.gapDistance);
  if (starter.fromStartEx != null && starter.gapDistanceEx != null)
    allOffsets.push(starter.fromStartEx + starter.gapDistanceEx);

  if (allOffsets.length === 0) return null;

  let minTime = Math.min(...allMillis);
  let maxTime = Math.max(...allMillis);
  let minOffset = Math.min(...allOffsets);
  let maxOffset = Math.max(...allOffsets);
  let timeRange = maxTime - minTime;
  let offsetRange = maxOffset - minOffset;
  minTime = minTime - 0.1 * timeRange;
  maxTime = maxTime + 0.1 * timeRange;
  minOffset = minOffset - 0.3 * offsetRange;
  maxOffset = maxOffset + 0.3 * offsetRange;
  timeRange = maxTime - minTime;
  offsetRange = maxOffset - minOffset;
  // Non-zero ranges, so toX/toY never divide by zero.
  if (timeRange === 0) {
    minTime -= OneMinuteInMillis;
    maxTime += OneMinuteInMillis;
    timeRange = maxTime - minTime;
  }
  if (offsetRange === 0) {
    minOffset -= 100;
    maxOffset += 100;
    offsetRange = maxOffset - minOffset;
  }
  const toX = (t: number) => (t - minTime) / timeRange;
  const toY = (offset: number) => (offset - minOffset) / offsetRange;
  return { minTime, maxTime, minOffset, maxOffset, timeRange, offsetRange, toX, toY };
}
